import ContentHeader from '@/components/ContentHeader';
import {
  ClockCircleOutlined,
  EnvironmentOutlined,
  MailOutlined,
  PhoneOutlined,
} from '@/components/Icons';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { modalType } from '@/components/Modal';
import { CDN2 } from '@/helpers/api';
import { basicPageProps, fetchTemplate } from '@/utils/isServer';
import { TRACKER_ENABLED } from '@/utils/tracker_enabled';
import { Button, Form, Input } from 'antd';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import Script from 'next/script';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next/';

const { TextArea } = Input;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { airports } = await basicPageProps(ctx);

  return {
    props: {
      airports,
    },
  };
};

export default function Reviews() {
  const [sendingForm, setSendingForm] = useState(false);
  const [form] = Form.useForm();
  const { t } = useTranslation();

  function handleSubmit(values: any) {
    setSendingForm(true);
    const params = {
      name: values.name,
      email: values.email,
      phone: values.phone,
      message: values.message,
    };

    fetch('/api/SendGrid/Contact', {
      ...fetchTemplate,
      method: 'POST',
      body: JSON.stringify(params),
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((success) => {
        console.log(success);
        setSendingForm(false);
        form.resetFields();
        modalType({
          type: 'success',
          title: 'Success',
          content: (
            <div className="text-left">
              <p>Contact Submitted</p>
              <p>
                Thank you for contacting us. A Parking Reservation Specialist
                will get back to you within 24 hours.
              </p>
            </div>
          ),
        });
      })
      .catch((error) => {
        console.log(error);
        setSendingForm(false);
      });
  }

  useEffect(() => {
    form.resetFields();
  }, [t]);

  return (
    <div>
      <Head>
        <title>
          Contact Our Parking Reservation Specialist | On Air Parking
        </title>
        <meta
          name="description"
          content="We have a Parking Reservation Specialist that's dedicated to making it simple and affordable to get you to the airport. E-mail, Call, Text or fill out the contact form below, and we'll assist you! Contact us."
          key="description"
        />
        <meta
          property="og:url"
          content="https://www.onairparking.com/contact"
          key="og:url"
        />
        <meta property="og:type" content="website" key="og:type" />
        <meta
          property="og:title"
          content="Contact Our Parking Reservation Specialist | On Air Parking"
          key="og:title"
        />
        <meta
          property="og:description"
          content="We have a Parking Reservation Specialist that's dedicated to making it simple and affordable to get you to the airport."
          key="og:description"
        />
        <meta
          property="og:image"
          content={CDN2() + '/staticmyapp/oglogo-white.png'}
          key="og:image"
        />
      </Head>
      <ContentHeader btitle={t('contact_us')} />
      <Script
        async
        strategy="afterInteractive"
        id="ze-snippet"
        src="https://static.zdassets.com/ekr/snippet.js?key=5124299d-2a72-4d79-874d-86c8849e83d3"
      />
      <LanguageSwitcher extraClass="mt-2" />

      <div className="bg-white">
        <div className="container max-w-4xl px-4 mx-auto py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14">
            <div>
              <p className="text-lg">{t('we_have_a_specialist')}</p>
              <p className="text-lg mt-4">{t('for_shuttle_service')}</p>
              <ul className="mt-8">
                <li className="flex flex-row items-center mb-5">
                  <PhoneOutlined className="mr-4 text-3xl mt-2 text-primary" />
                  <div>
                    <p>
                      {t('dial')}: 888-48-PARKING (
                      <a
                        href="tel:8884872754"
                        className="text-primary underline"
                      >
                        888-487-2754
                      </a>
                      )
                    </p>
                    <p>{t('text')}: 424-532-8940</p>
                  </div>
                </li>
                <li className="flex flex-row items-center mb-5">
                  <MailOutlined className="mr-4 text-3xl mt-2 text-primary" />
                  <a
                    href="mailto:hello@noson.io"
                    className="text-primary underline"
                  >
                    hello@noson.io
                  </a>
                </li>
                <li className="flex flex-row items-center mb-5">
                  <EnvironmentOutlined className="mr-4 text-3xl mt-2 text-primary" />
                  <p>
                    1014 S. Westlake Blvd, Suite 14-333, Westlake Village, CA
                    91361
                  </p>
                </li>
                <li className="flex flex-row items-center mb-5">
                  <ClockCircleOutlined className="mr-4 text-3xl mt-2 text-primary" />
                  <p>{t('timeout')}</p>
                </li>
              </ul>
            </div>

            <div className="p-4 rounded-md w-full border mx-auto bg-gray-100">
              <Form
                form={form}
                id="crypto-form"
                className=""
                layout="vertical"
                onFinish={handleSubmit}
              >
                <Form.Item
                  name="name"
                  label={t('name')}
                  rules={[
                    {
                      type: 'string',
                      required: true,
                      message: t('name_message'),
                    },
                  ]}
                  htmlFor="name"
                >
                  <Input
                    className="rounded-full"
                    id="name"
                    name="name"
                    type="text"
                  />
                </Form.Item>
                <Form.Item
                  name="email"
                  label={t('email')}
                  rules={[
                    {
                      type: 'email',
                      required: true,
                      message: t('email_message'),
                    },
                  ]}
                  htmlFor="email"
                >
                  <Input
                    className="rounded-full"
                    id="email"
                    name="email"
                    type="text"
                  />
                </Form.Item>
                <Form.Item
                  name="phone"
                  label={t('phone')}
                  rules={[
                    {
                      type: 'string',
                      required: true,
                      message: t('phone_message'),
                    },
                  ]}
                  htmlFor="phone"
                >
                  <Input
                    className="rounded-full"
                    id="phone"
                    name="phone"
                    type="text"
                  />
                </Form.Item>
                <Form.Item
                  name="message"
                  label={t('message')}
                  rules={[
                    {
                      type: 'string',
                      required: true,
                      message: t('message_message'),
                    },
                  ]}
                  htmlFor="message"
                >
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    className={`w-full border rounded-md border-gray-250 ring-0 hover:border-primary focus:shadow-md focus:border-none focus:ring-2 focus:border-primary focus:ring-yellow-500 focus:ring-opacity-20 focus:outline-none ${
                      form.getFieldError('message').length > 0
                        ? 'border-red-500 shadow-md ring-2 ring-red-200'
                        : ''
                    }`}
                    spellCheck="false"
                  />
                </Form.Item>
                <Button
                  htmlType="submit"
                  type="primary"
                  size="large"
                  className="px-8 mx-auto bg-primary block uppercase rounded-full mt-8 mb-4"
                  loading={sendingForm}
                >
                  {t('submit')}
                </Button>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
